import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'

/**
 * LA APP DEL TÉCNICO SE ACTUALIZA SOLA (4 nov 2026).
 *
 * Johan: «¿las versiones en el celular del técnico no se pueden actualizar
 * solas?». No podían: la APK lleva la web por dentro y cada arreglo exigía
 * instalar una APK a mano en cada teléfono. Ahora, en cada push, Netlify
 * publica la web empaquetada (scripts/empaquetar-ota.mjs) y la app:
 *
 *   1 · revisa al abrir y al volver a ella si hay una versión nueva, y la baja
 *       por detrás;
 *   2 · la estrena al arrancar en frío o al volver después de una hora fuera.
 *       NUNCA al salir de la app: salir es también abrir la cámara, y
 *       recargar ahí perdía la foto y lo que estuviera escribiendo;
 *   3 · si la versión nueva no arranca, el plugin vuelve sola a la anterior y
 *       esa versión no se vuelve a intentar.
 *
 * Lo nativo —plugins, permisos, Java— sigue necesitando APK: cada paquete
 * dice desde qué APK sirve y las más viejas no lo bajan.
 */

const ORIGEN = 'https://app.megadistribucionesyproyectos.com'
/** La versión ya bajada y lista para estrenar: { id, version }. */
const LISTA = 'mega-ota-lista'
const FUERA_LARGO_MS = 60 * 60 * 1000
const CADA_CUANTO_MS = 10 * 60 * 1000

type Publicada = { version: string; url: string; nativoMinimo: number }
type Lista = { id: string; version: string }

function esAndroidNativo(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
}

function leerLista(): Lista | null {
  try {
    const crudo = localStorage.getItem(LISTA)
    return crudo ? (JSON.parse(crudo) as Lista) : null
  } catch {
    return null
  }
}

function guardarLista(lista: Lista | null) {
  try {
    if (lista) localStorage.setItem(LISTA, JSON.stringify(lista))
    else localStorage.removeItem(LISTA)
  } catch {
    // Sin almacenamiento se estrena en la próxima revisión; no es grave.
  }
}

async function leerJson<T>(url: string): Promise<T | null> {
  const corte = new AbortController()
  const reloj = setTimeout(() => corte.abort(), 10_000)
  try {
    const respuesta = await fetch(url, { cache: 'no-store', signal: corte.signal })
    return respuesta.ok ? ((await respuesta.json()) as T) : null
  } catch {
    return null
  } finally {
    clearTimeout(reloj)
  }
}

/** Estrena la versión bajada, si hay una y sirve. Recarga la app. */
async function estrenarSiHayLista(): Promise<boolean> {
  const lista = leerLista()
  if (!lista) return false

  const { bundle } = await CapacitorUpdater.current()
  if (bundle.version === lista.version) {
    guardarLista(null)
    return false
  }

  // Si ya falló una vez, el plugin la marcó y no se insiste: sin esto, una
  // versión rota recargaría la app en cada arranque.
  const { bundles } = await CapacitorUpdater.list()
  const paquete = bundles.find((b) => b.id === lista.id)
  if (!paquete || paquete.status === 'error' || paquete.status === 'deleted') {
    guardarLista(null)
    return false
  }

  await CapacitorUpdater.set({ id: paquete.id })
  return true
}

let revisando = false
let ultimaRevision = 0

/** Mira si hay versión nueva y la baja por detrás. Sin señal no pasa nada. */
async function revisar() {
  if (revisando || Date.now() - ultimaRevision < CADA_CUANTO_MS) return
  revisando = true
  ultimaRevision = Date.now()

  try {
    // El ?t= es para que ningún caché conteste por la red ni por el teléfono.
    const t = Date.now()
    const [mia, publicada] = await Promise.all([
      leerJson<{ version: string }>(`/version-web.json?t=${t}`),
      leerJson<Publicada>(`${ORIGEN}/ota/version.json?t=${t}`),
    ])

    if (!publicada?.version || publicada.version === mia?.version) return
    if (leerLista()?.version === publicada.version) return

    // Pide una APK más nueva que esta: se queda con lo que tiene.
    const { build } = await App.getInfo()
    if (Number(build) < publicada.nativoMinimo) return

    const { bundles } = await CapacitorUpdater.list()
    if (bundles.some((b) => b.version === publicada.version && b.status === 'error')) return

    const paquete =
      bundles.find((b) => b.version === publicada.version && b.status === 'pending') ??
      (await CapacitorUpdater.download({
        url: new URL(publicada.url, ORIGEN).href,
        version: publicada.version,
      }))

    guardarLista({ id: paquete.id, version: publicada.version })
  } catch {
    // Sin señal o con la descarga cortada: se intenta en la próxima revisión.
  } finally {
    revisando = false
  }
}

export function iniciarActualizaciones() {
  if (!esAndroidNativo()) return

  // Primero que nada: decirle al plugin que esta versión arrancó. Si no lo
  // oye a tiempo, la da por rota y vuelve a la anterior.
  void CapacitorUpdater.notifyAppReady()
    .then(() => estrenarSiHayLista())
    .then((estrenada) => {
      if (!estrenada) void revisar()
    })
    .catch(() => undefined)

  let fueraDesde: number | null = null

  void App.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) {
      fueraDesde = Date.now()
      return
    }

    const largo = fueraDesde !== null && Date.now() - fueraDesde >= FUERA_LARGO_MS
    fueraDesde = null

    void (largo ? estrenarSiHayLista() : Promise.resolve(false))
      .then((estrenada) => {
        if (!estrenada) void revisar()
      })
      .catch(() => undefined)
  })
}
